import type {
  IntelTechnologiesSelectDto,
  IntelTechnologiesSortBy,
  IntelTechnologyCompaniesSelectDto,
  SortDirection,
} from '@@types/api.types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

type Nullable<T> = T | null | undefined;

export type IntelTechnologiesQueryParams = {
  categoryIds?: Nullable<string[]>;
  challengeId?: Nullable<string>;
  challengePhaseId?: Nullable<string>;
  companyIds?: Nullable<string[]>;
  domainIds?: Nullable<string[]>;
  enabled?: boolean;
  limit?: Nullable<number>;
  mrlMax?: Nullable<number>;
  mrlMin?: Nullable<number>;
  page?: Nullable<number>;
  search?: Nullable<string>;
  sortBy?: Nullable<IntelTechnologiesSortBy>;
  sortDirection?: Nullable<SortDirection>;
  subcategoryIds?: Nullable<string[]>;
  trlMax?: Nullable<number>;
  trlMin?: Nullable<number>;
  useIds?: Nullable<string[]>;
};

const buildQueryParams = (params: IntelTechnologiesQueryParams) => {
  const queryParams: Record<string, unknown> = {};

  if (params.categoryIds && params.categoryIds.length > 0) {
    queryParams.categoryIds = params.categoryIds;
  }

  if (params.challengeId) {
    queryParams.challengeId = params.challengeId;
  }

  if (params.challengePhaseId) {
    queryParams.challengePhaseId = params.challengePhaseId;
  }

  if (params.companyIds && params.companyIds.length > 0) {
    queryParams.companyIds = params.companyIds;
  }

  if (params.domainIds && params.domainIds.length > 0) {
    queryParams.domainIds = params.domainIds;
  }

  if (params.limit) {
    queryParams.limit = params.limit;
  }

  if (params.mrlMax) {
    queryParams.mrlMax = params.mrlMax;
  }

  if (params.mrlMin) {
    queryParams.mrlMin = params.mrlMin;
  }

  if (params.page) {
    queryParams.page = params.page;
  }

  if (params.search) {
    queryParams.search = params.search;
  }

  if (params.sortBy) {
    queryParams.sortBy = params.sortBy;
  }

  if (params.sortDirection) {
    queryParams.sortDirection = params.sortDirection;
  }

  if (params.subcategoryIds && params.subcategoryIds.length > 0) {
    queryParams.subcategoryIds = params.subcategoryIds;
  }

  if (params.trlMax) {
    queryParams.trlMax = params.trlMax;
  }

  if (params.trlMin) {
    queryParams.trlMin = params.trlMin;
  }

  if (params.useIds && params.useIds.length > 0) {
    queryParams.useIds = params.useIds;
  }

  return queryParams;
};

export const useIntelTechnologiesQuery = (params: IntelTechnologiesQueryParams) => {
  const { apiGet } = useApi();
  const { enabled = true } = params;
  const queryParams = buildQueryParams(params);

  return useQuery<IntelTechnologiesSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<IntelTechnologiesSelectDto>({
        path: '/v1/intel/technologies',
        query: queryParams,
      });
    },
    queryKey: queryKeys.intelTechnologies.getTechnologies(queryParams),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  });
};

export const useIntelTechnologyCompaniesQuery = (params?: { enabled?: boolean }) => {
  const { apiGet } = useApi();
  const { enabled = true } = params ?? {};

  return useQuery<IntelTechnologyCompaniesSelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<IntelTechnologyCompaniesSelectDto>({
        path: '/v1/intel/technologies/companies',
      });
    },
    queryKey: queryKeys.intelTechnologies.getTechnologyCompanies,
    refetchOnWindowFocus: true,
  });
};
